import type { DateRange } from "react-day-picker";

import { MS_PER_DAY } from "@/lib/constants";
import { dates } from "@/lib/dates";
import { DateFilterOption, type LeaderboardQueryParams } from "@/types";

export type RacetrackGoalScope = "WEEK" | "MONTH";

export interface RacetrackPeriodResolution {
  // Null when timeframe is greater than a month
  goalScope: RacetrackGoalScope | null;
  goalMonth: number; // 1-12
  goalYear: number;
}

const WEEK_DAYS = 7;
const MAX_MONTH_DAYS = 31;

const weekResolution = (anchor: Date): RacetrackPeriodResolution => ({
  goalScope: "WEEK",
  goalMonth: anchor.getMonth() + 1,
  goalYear: anchor.getFullYear(),
});

const monthResolution = (anchor: Date): RacetrackPeriodResolution => ({
  goalScope: "MONTH",
  goalMonth: anchor.getMonth() + 1,
  goalYear: anchor.getFullYear(),
});

const noGoalResolution = (anchor: Date): RacetrackPeriodResolution => ({
  goalScope: null,
  goalMonth: anchor.getMonth() + 1,
  goalYear: anchor.getFullYear(),
});

// Inclusive day count between two dates (same day = 1).
const inclusiveDaySpan = (from: Date, to: Date): number =>
  Math.round((to.getTime() - from.getTime()) / MS_PER_DAY) + 1;

const resolveCustom = (
  range: DateRange | undefined,
  now: Date,
): RacetrackPeriodResolution => {
  const from = range?.from;
  const to = range?.to;
  if (!from || !to) return noGoalResolution(now);

  const span = inclusiveDaySpan(from, to);
  if (span <= WEEK_DAYS) return weekResolution(from);
  if (span <= MAX_MONTH_DAYS) return monthResolution(from);
  return noGoalResolution(now);
};

export const leaderboardGoal = {
  // Maps the leaderboard's selected period to which goal the racetrack shows.
  // Weeks always anchor to the month their first day (Sunday) falls in.
  resolvePeriod: (
    params: LeaderboardQueryParams,
  ): RacetrackPeriodResolution => {
    const now = new Date();

    switch (params.timePeriod) {
      case DateFilterOption.Today:
        return weekResolution(now);
      case DateFilterOption.Yesterday: {
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        return weekResolution(yesterday);
      }
      case DateFilterOption.ThisWeek:
        return weekResolution(dates.getWeekStartDate(now));
      case DateFilterOption.LastWeek: {
        const lastWeekStart = dates.getWeekStartDate(now);
        lastWeekStart.setDate(lastWeekStart.getDate() - WEEK_DAYS);
        return weekResolution(lastWeekStart);
      }
      case DateFilterOption.ThisMonth:
        return monthResolution(now);
      case DateFilterOption.LastMonth:
        return monthResolution(
          new Date(now.getFullYear(), now.getMonth() - 1, 1),
        );
      case DateFilterOption.LastQuarter:
      case DateFilterOption.ThisQuarter:
      case DateFilterOption.ThisYear:
      case DateFilterOption.AllTime:
        return noGoalResolution(now);
      case DateFilterOption.Custom:
        return resolveCustom(params.dateRange, now);
    }
  },
};
